import { Button } from '@cottage/design-system/internal-tooling/ui'
import { DrawerShell } from '@/tableControls'
import type { RollForwardFailure, RollForwardSummary } from '@/services/projections'
import { monthLabel } from './monthColumns'
import { formatCurrencyWhole, formatMonthLabel } from './projectionsFormat'

export type RollForwardChoice = 'keep' | 'reanchor' | 'hold'

export type RollForwardResultDialogProps = {
  summary: RollForwardSummary
  lastClosedMonth: string
  recipeCount: number
  failures: RollForwardFailure[]
  importRevertible: boolean
  choice: RollForwardChoice
  onSelectKeep: () => void
  onSelectReanchor: () => void
  onSelectHold: () => void
  onRetry: (ids: number[]) => void
  onDiscardImport: () => void
  onClose: () => void
}

function plural(count: number, singular: string, pluralForm: string): string {
  return count === 1 ? singular : pluralForm
}

function ChoiceOption({
  label,
  helper,
  selected,
  onClick,
}: {
  label: string
  helper: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <div>
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        onClick={onClick}
        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
      >
        <span
          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
            selected ? 'border-brand-600' : 'border-gray-300'
          }`}
          aria-hidden
        >
          {selected ? <span className="h-2 w-2 rounded-full bg-brand-600" /> : null}
        </span>
        {label}
      </button>
      <p className="mt-1 pl-6 text-xs text-gray-500">{helper}</p>
    </div>
  )
}

export default function RollForwardResultDialog({
  summary,
  lastClosedMonth,
  recipeCount,
  failures,
  importRevertible,
  choice,
  onSelectKeep,
  onSelectReanchor,
  onSelectHold,
  onRetry,
  onDiscardImport,
  onClose,
}: RollForwardResultDialogProps) {
  const newMonth = formatMonthLabel(monthLabel(lastClosedMonth, 0))
  const oldMonth = formatMonthLabel(monthLabel(lastClosedMonth, -summary.k_months))
  const overrideWord = plural(
    summary.recipe_overrides_dropped,
    'recipe override',
    'recipe overrides',
  )
  const oneTimeWord = plural(summary.one_time_items_dropped, 'one-time item', 'one-time items')

  const showDroppedLine =
    summary.one_time_items_dropped > 0 || summary.recipe_overrides_dropped > 0

  const keepHelper =
    recipeCount >= 1
      ? 'Leave recipe-driven lines on their current path. Projected dollars for open months re-derive from the new actuals.'
      : 'Keep the rolled-forward projected dollars as they are now.'

  return (
    <DrawerShell
      title="Review roll-forward"
      onClose={onClose}
      footer={
        <div>
          <Button
            variant="danger"
            size="md"
            className="w-full"
            disabled={!importRevertible}
            onClick={onDiscardImport}
          >
            Revert
          </Button>
          <p className="mt-1 text-xs text-gray-500">
            {importRevertible
              ? 'Restore the grid and every case to how they were before this Update Historicals. Snapshots for the newly closed month are removed. Any Save on a rolled-forward case after the update is also undone.'
              : 'This update ran before restore snapshots were kept, so it cannot be discarded. The next Update Historicals can.'}
          </p>
        </div>
      }
    >
      <div className="space-y-3 text-sm text-gray-700">
        <p>
          Historicals now run through {newMonth} (was {oldMonth}). {newMonth} is an actuals column.
          Assumptions for still-open months stayed put; choose below how this case's projected
          dollars should be driven.
        </p>
        <p>
          Close this drawer to review the grid. The case stays unsaved until you Save on the page.
        </p>
        {showDroppedLine ? (
          <p>
            Dropped {summary.one_time_items_dropped} {oneTimeWord} and{' '}
            {summary.recipe_overrides_dropped} {overrideWord} that fell into now-actual months.
          </p>
        ) : null}
        {summary.cash_policy_one_time_cleared.map((item) => {
          const amountNum = Number(item.amount)
          const amountLabel = Number.isFinite(amountNum)
            ? formatCurrencyWhole(amountNum)
            : String(item.amount ?? '')
          const distClause =
            item.dist_num != null ? ` (distribution #${item.dist_num})` : ''
          return (
            <p key={`${item.case_id}-${item.date}`}>
              Cleared spent one-time extra on {item.case_name}: {amountLabel} dated {item.date}
              {distClause}.
            </p>
          )
        })}
        {summary.cash_policy_schedules_recomputed > 0 ? (
          <p>
            Waterfall split recomputed for {summary.cash_policy_schedules_recomputed}{' '}
            {plural(
              summary.cash_policy_schedules_recomputed,
              'case',
              'cases',
            )}
            .
          </p>
        ) : null}
        {summary.cash_policy_schedule_failures.length > 0 ? (
          <ul className="space-y-1 text-sm text-red-600">
            {summary.cash_policy_schedule_failures.map((failure) => (
              <li key={`cp-sched-${failure.case_id}`}>
                {failure.case_name}: {failure.error}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {failures.length > 0 ? (
        <div className="mt-4 space-y-2">
          <ul className="space-y-1 text-sm text-red-600">
            {failures.map((failure) => (
              <li key={failure.case_id}>
                {failure.case_name}: {failure.error}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div
        className="mt-6 flex flex-col gap-4"
        role="radiogroup"
        aria-label="How to drive projected values"
      >
        <ChoiceOption
          label="Keep values"
          selected={choice === 'keep'}
          onClick={onSelectKeep}
          helper={keepHelper}
        />
        {recipeCount >= 1 ? (
          <ChoiceOption
            label="Re-anchor recipes"
            selected={choice === 'reanchor'}
            onClick={onSelectReanchor}
            helper={`Rebuild every recipe-driven line from actuals through ${newMonth}. Those projected dollars will change on the grid.`}
          />
        ) : null}
        <ChoiceOption
          label="Hold values for specific lines…"
          selected={choice === 'hold'}
          onClick={onSelectHold}
          helper="Pick lines whose projected dollars should stay exactly as they were before Update Historicals, instead of re-deriving from the new actuals."
        />
      </div>

      {failures.length > 0 ? (
        <div className="mt-4">
          <Button
            variant="outline"
            size="md"
            className="w-full"
            onClick={() => onRetry(failures.map((f) => f.case_id))}
          >
            Retry roll-forward
          </Button>
          <p className="mt-1 text-xs text-gray-500">
            Run roll-forward again for the cases that failed.
          </p>
        </div>
      ) : null}
    </DrawerShell>
  )
}
